import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Link as LinkIcon, Send } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { cn } from '@/lib/utils';
import type { ContentJson } from '@/types/feed';

interface CommentEditorProps {
  onSubmit: (html: string, json: ContentJson) => void;
  placeholder?: string;
  className?: string;
  isSubmitting?: boolean;
}

export default function CommentEditor({ onSubmit, placeholder, className, isSubmitting }: CommentEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder || 'Scrivi un commento...' }),
    ],
    content: '',
    editorProps: {
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey) {
          event.preventDefault();
          handleSubmit();
          return true;
        }
        return false;
      },
    },
  });

  function handleSubmit() {
    if (!editor || editor.isEmpty || isSubmitting) return;
    onSubmit(editor.getHTML(), editor.getJSON());
    editor.commands.clearContent();
  }

  if (!editor) return null;

  return (
    <div className={cn("flex items-end gap-2", className)}>
      <div className="flex-1 border rounded-lg overflow-hidden bg-white dark:bg-gray-900">
        <div className="border-b bg-gray-50 dark:bg-gray-800/50 p-0.5 flex items-center gap-0.5">
          <Button type="button" variant="ghost" size="sm" className={cn("h-7 w-7 p-0", editor.isActive('bold') && "bg-blue-100 text-blue-700")}
            onClick={() => editor.chain().focus().toggleBold().run()} title="Grassetto">
            <Bold className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className={cn("h-7 w-7 p-0", editor.isActive('italic') && "bg-blue-100 text-blue-700")}
            onClick={() => editor.chain().focus().toggleItalic().run()} title="Corsivo">
            <Italic className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className={cn("h-7 w-7 p-0", editor.isActive('link') && "bg-blue-100 text-blue-700")}
            onClick={() => {
              const url = window.prompt('URL:', 'https://');
              if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            }} title="Link">
            <LinkIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
        <EditorContent
          editor={editor}
          className={cn(
            "prose prose-sm dark:prose-invert max-w-none px-3 py-2",
            "[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[36px] [&_.ProseMirror]:max-h-[120px] [&_.ProseMirror]:overflow-y-auto",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0",
          )}
        />
      </div>
      <Button
        onClick={handleSubmit}
        disabled={!editor || editor.isEmpty || isSubmitting}
        size="sm"
        className="h-9 w-9 p-0 shrink-0"
      >
        <Send className="h-4 w-4" />
      </Button>
    </div>
  );
}
